import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';

const appointmentTypes = [
  { value: 'doctor_visit', label: 'Doctor Visit' },
  { value: 'medication_refill', label: 'Medication Refill' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'lab_test', label: 'Lab Test' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'other', label: 'Other' },
];

const AddAppointmentDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    appointment_type: 'doctor_visit',
    date: '',
    time: '',
    doctor_name: '',
    clinic_name: '',
    clinic_address: '',
    clinic_phone: '',
    notes: '',
    reminder_days_before: 1,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      appointment_type: 'doctor_visit',
      date: '',
      time: '',
      doctor_name: '',
      clinic_name: '',
      clinic_address: '',
      clinic_phone: '',
      notes: '',
      reminder_days_before: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        time: formData.time || null,
        doctor_name: formData.doctor_name || null,
        clinic_name: formData.clinic_name || null,
        clinic_address: formData.clinic_address || null,
        clinic_phone: formData.clinic_phone || null,
        notes: formData.notes || null,
      };
      
      await API.post('/medical/appointments', dataToSend);
      toast.success('Appointment scheduled successfully');
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to create appointment:', error);
      toast.error('Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Appointment</DialogTitle>
          <DialogDescription>
            Add a new medical appointment, medication refill reminder, or follow-up
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Annual Physical, Prescription Refill"
                data-testid="input-appointment-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select 
                value={formData.appointment_type} 
                onValueChange={(v) => handleChange('appointment_type', v)}
              >
                <SelectTrigger data-testid="select-appointment-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {appointmentTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                data-testid="input-appointment-date"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
                data-testid="input-appointment-time"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reminder">Remind me</Label>
              <Select 
                value={String(formData.reminder_days_before)} 
                onValueChange={(v) => handleChange('reminder_days_before', parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Day of</SelectItem>
                  <SelectItem value="1">1 day before</SelectItem>
                  <SelectItem value="2">2 days before</SelectItem>
                  <SelectItem value="3">3 days before</SelectItem>
                  <SelectItem value="7">1 week before</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Location Details (Optional)</h4>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="doctor_name">Doctor Name</Label>
                <Input
                  id="doctor_name"
                  value={formData.doctor_name}
                  onChange={(e) => handleChange('doctor_name', e.target.value)}
                  placeholder="Dr. Smith"
                  data-testid="input-appointment-doctor"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="clinic_name">Clinic / Pharmacy</Label>
                <Input
                  id="clinic_name"
                  value={formData.clinic_name}
                  onChange={(e) => handleChange('clinic_name', e.target.value)}
                  placeholder="Family Medical Center"
                  data-testid="input-appointment-clinic"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="clinic_address">Address</Label>
                <Input
                  id="clinic_address"
                  value={formData.clinic_address}
                  onChange={(e) => handleChange('clinic_address', e.target.value)}
                  placeholder="123 Main St"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="clinic_phone">Phone</Label>
                <Input
                  id="clinic_phone"
                  value={formData.clinic_phone}
                  onChange={(e) => handleChange('clinic_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional details..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
              data-testid="submit-appointment-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Schedule Appointment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAppointmentDialog;
